import { useEffect, useRef, useState } from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { useTranslation } from "react-i18next";
import TimeseriesExcelDownloadBtn from "components/project/TimeseriesExcelDownloadBtn";
import TimeseriesHmlDownloadBtn from "components/project/TimeseriesHmlDownloadBtn";
import { useAppStore } from "lib/store/useAppStore";
import { getFile } from "lib/api/getFile";
import { getFileList } from "lib/api/getfileList";
import { getImageBase64 } from "lib/api/getImageBase64";
import { useClickOutSide } from "lib/hooks/useClickOutside";
import { normalizeFusionResultCode } from "lib/utils/fusionResult";
import TimeseriesAnalyticsSection from "./components/TimeseriesAnalyticsSection";
import TimeseriesConfigBar from "./components/TimeseriesConfigBar";
import TimeseriesDataTableGroup from "./components/TimeseriesDataTableGroup";
import TimeseriesEditModal from "./components/TimeseriesEditModal";
import TimeseriesPreviewModal from "./components/TimeseriesPreviewModal";
import { buildTimeseriesComprehensiveOpinion } from "./utils/buildTimeseriesComprehensiveOpinion";
import { buildSingleMetricChartData, buildChartOptions } from "./utils/timeseriesChart";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

// HELPER: 비어 있는 표시값을 공통 fallback 텍스트로 정리한다.
function getDisplayText(value, fallback = "-") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

// HELPER: 측정 문자열에서 숫자만 추출해 차트/통계 계산에 쓸 수 있게 변환한다.
function toNumeric(value) {
  const parsed = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMetric(value, suffix = "", digits = 1) {
  const numeric = toNumeric(value);
  if (numeric == null) return "-";
  return `${numeric.toFixed(digits)}${suffix}`;
}

function formatThreePhaseDifference(
  clickPoints,
  { digits = 1, emptyText = "N/A", unit = " °C" } = {},
) {
  const values = Array.isArray(clickPoints)
    ? clickPoints
        .map((point) => toNumeric(point?.value))
        .filter((value) => value != null)
    : [];

  if (values.length === 0) return emptyText;

  const difference = Math.max(...values) - Math.min(...values);
  return `${difference.toFixed(digits)}${unit}`;
}

function getThreePhaseDifferenceValue(clickPoints) {
  const values = Array.isArray(clickPoints)
    ? clickPoints
        .map((point) => toNumeric(point?.value))
        .filter((value) => value != null)
    : [];

  if (values.length === 0) {
    return null;
  }

  return Math.max(...values) - Math.min(...values);
}

function averageNumeric(values, digits = 1) {
  const validValues = values.filter((value) => value != null);
  if (validValues.length === 0) {
    return "-";
  }

  const average =
    validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
  return average.toFixed(digits);
}

function getCoverageTone(coveragePercent) {
  if (coveragePercent >= 70) return "isHigh";
  if (coveragePercent >= 40) return "isMedium";
  return "isLow";
}

function getAnalysisDateLabel(item) {
  return getDisplayText(
    item?.shooting_date || item?.uploaded_at,
    item?.file_name,
  );
}

// HELPER: 선택 기간 중 가장 큰 변화가 있었던 구간을 찾아 요약 문장 계산에 재사용한다.
function findLargestChangePeriod(items, getValue) {
  let largestChange = null;

  for (let index = 1; index < items.length; index += 1) {
    const previousItem = items[index - 1];
    const currentItem = items[index];
    const previousValue = getValue(previousItem);
    const currentValue = getValue(currentItem);

    if (previousValue == null || currentValue == null) {
      continue;
    }

    const difference = Math.abs(currentValue - previousValue);

    if (!largestChange || difference > largestChange.difference) {
      largestChange = {
        difference,
        previousItem,
        currentItem,
      };
    }
  }

  return largestChange;
}

function getUltrasonicJudgementText(difference, t) {
  if (difference == null) {
    return t("imageFileIndex.timeseries.opinion.ultrasonicInsufficientData", {
      defaultValue:
        "초음파 변화 판단에 필요한 비교 데이터가 충분하지 않습니다.",
    });
  }

  if (difference >= 10) {
    return t("imageFileIndex.timeseries.opinion.ultrasonicAbnormal", {
      value: difference.toFixed(1),
      defaultValue:
        "초음파 편차가 {{value}} dB로 10 dB 이상이므로 이상 징후로 판단됩니다.",
    });
  }

  if (difference >= 5) {
    return t("imageFileIndex.timeseries.opinion.ultrasonicCaution", {
      value: difference.toFixed(1),
      defaultValue:
        "초음파 편차가 {{value}} dB로 5 dB 이상이므로 해당 설비는 요주의 관찰이 필요합니다.",
    });
  }

  return t("imageFileIndex.timeseries.opinion.ultrasonicStable", {
    value: difference.toFixed(1),
    defaultValue:
      "초음파 편차는 {{value}} dB로 5 dB 미만이며 비교적 안정적인 흐름입니다.",
  });
}

function buildUltrasonicOpinion(sortedFiles, t) {
  const averageUltrasonic = averageNumeric(
    sortedFiles.map((item) => toNumeric(item?.avg_decibel)),
  );
  const largestUltrasonicChange = findLargestChangePeriod(sortedFiles, (item) =>
    toNumeric(item?.avg_decibel),
  );

  if (averageUltrasonic === "-") {
    return t("imageFileIndex.timeseries.opinion.ultrasonicNoAverage", {
      defaultValue:
        "데이터를 분석한 결과 초음파 평균값을 산출할 수 없어 추가 측정 데이터 확인이 필요합니다.",
    });
  }

  if (!largestUltrasonicChange) {
    return t("imageFileIndex.timeseries.opinion.ultrasonicNoTrend", {
      average: averageUltrasonic,
      defaultValue:
        "데이터를 분석한 결과 평균 발생 초음파는 {{average}} dB입니다. 기간 간 비교 가능한 초음파 데이터가 부족해 변화 추세 판단은 보류했습니다.",
    });
  }

  const rangeText = t("imageFileIndex.timeseries.opinion.range", {
    from: getAnalysisDateLabel(largestUltrasonicChange.previousItem),
    to: getAnalysisDateLabel(largestUltrasonicChange.currentItem),
    defaultValue: "{{from}}부터 {{to}}까지",
  });

  return t("imageFileIndex.timeseries.opinion.ultrasonicSummary", {
    average: averageUltrasonic,
    range: rangeText,
    difference: largestUltrasonicChange.difference.toFixed(1),
    judgement: getUltrasonicJudgementText(
      largestUltrasonicChange.difference,
      t,
    ),
    defaultValue:
      "데이터를 분석한 결과 평균 발생 초음파는 {{average}} dB이며, 가장 큰 변화는 {{range}} {{difference}} dB 차이로 확인됩니다.\n{{judgement}}",
  });
}

function buildTemperatureOpinion(
  sortedFiles,
  averageThreePhaseDifferenceText,
  t,
) {
  const averageTemperature = averageNumeric(
    sortedFiles.map((item) => toNumeric(item?.thermal_center)),
  );
  const largestTemperatureChange = findLargestChangePeriod(
    sortedFiles,
    (item) => toNumeric(item?.thermal_center),
  );

  if (averageTemperature === "-") {
    return t("imageFileIndex.timeseries.opinion.temperatureNoAverage", {
      defaultValue:
        "검출 온도 데이터가 부족해 평균 온도와 기간별 온도 변화 의견을 생성할 수 없습니다.",
    });
  }

  const temperatureTrendText = largestTemperatureChange
    ? t("imageFileIndex.timeseries.opinion.temperatureTrend", {
        from: getAnalysisDateLabel(largestTemperatureChange.previousItem),
        to: getAnalysisDateLabel(largestTemperatureChange.currentItem),
        difference: largestTemperatureChange.difference.toFixed(1),
        defaultValue:
          "{{from}}부터 {{to}}까지 {{difference}} °C 차이로 가장 크게 변화했습니다.",
      })
    : t("imageFileIndex.timeseries.opinion.temperatureNoTrend", {
        defaultValue:
          "기간 간 비교 가능한 온도 데이터가 부족해 최대 변화 구간은 확인되지 않았습니다.",
      });

  const threePhaseText =
    averageThreePhaseDifferenceText === "-"
      ? t("imageFileIndex.timeseries.opinion.temperatureNoThreePhase", {
          defaultValue:
            "3상 비교 분석이 수행된 데이터가 없어 온도차 평가는 제외했습니다.",
        })
      : t("imageFileIndex.timeseries.opinion.temperatureAverageThreePhase", {
          value: averageThreePhaseDifferenceText,
          defaultValue:
            "3상 비교 분석이 수행된 데이터의 평균 온도차는 {{value}}입니다.",
        });

  return t("imageFileIndex.timeseries.opinion.temperatureSummary", {
    average: averageTemperature,
    trend: temperatureTrendText,
    threePhase: threePhaseText,
    defaultValue:
      "검출 온도를 분석한 결과 평균 온도는 {{average}} °C이며, {{trend}}\n{{threePhase}}",
  });
}

function parseDateValue(value) {
  if (!value) return null;

  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) {
    return direct;
  }

  const match = String(value).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function compareByShootingDate(a, b) {
  const aDate = parseDateValue(a?.shooting_date || a?.uploaded_at);
  const bDate = parseDateValue(b?.shooting_date || b?.uploaded_at);

  if (aDate && bDate) return aDate - bDate;
  if (aDate) return -1;
  if (bDate) return 1;

  return String(a?.file_name ?? "").localeCompare(String(b?.file_name ?? ""));
}

function getUploadId(item) {
  return String(item?.upload_id ?? item?.id ?? "");
}

function normalizeFusionStatus(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  if (normalized === "0" || normalized === "normal" || normalized === "정상") {
    return "normal";
  }

  if (
    normalized === "1" ||
    normalized === "caution" ||
    normalized === "warning" ||
    normalized === "요주의"
  ) {
    return "warning";
  }

  if (
    normalized === "2" ||
    normalized === "abnormal" ||
    normalized === "fault" ||
    normalized === "이상"
  ) {
    return "abnormal";
  }

  return "";
}

function normalizeFusionStatusCategory(value) {
  const code = normalizeFusionResultCode(value);

  if (code === 0) {
    return "normal";
  }

  if (code === 1) {
    return "warning";
  }

  if (code === 2 || code === 3) {
    return "abnormal";
  }

  return normalizeFusionStatus(value);
}

// eslint-disable-next-line no-unused-vars
function buildCandidateLabel(item, currentUploadId, t) {
  const date = getDisplayText(
    item?.shooting_date,
    t("imageFileIndex.timeseries.messages.noDate", {
      defaultValue: "날짜 없음",
    }),
  );
  const name = getDisplayText(
    item?.file_name,
    t("imageFileIndex.timeseries.messages.unnamedFile", {
      defaultValue: "이름 없는 파일",
    }),
  );

  if (getUploadId(item) === String(currentUploadId ?? "")) {
    return t("imageFileIndex.timeseries.messages.currentFileLabel", {
      date,
      name,
      defaultValue: "{{date}} | {{name}} | 현재 파일",
    });
  }

  return `${date} | ${name}`;
}

function getCandidateLocationText(item) {
  return [item?.building_name, item?.floor, item?.location]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function buildCandidateOptionParts(item, candidateOptionFields) {
  const locationText = getCandidateLocationText(item);

  return [
    {
      key: candidateOptionFields[0].key,
      label: candidateOptionFields[0].label,
      value: getDisplayText(item?.uploaded_at),
    },
    {
      key: candidateOptionFields[1].key,
      label: candidateOptionFields[1].label,
      value: getDisplayText(item?.file_name),
    },
    {
      key: candidateOptionFields[2].key,
      label: candidateOptionFields[2].label,
      value: getDisplayText(item?.facility_type),
    },
    {
      key: candidateOptionFields[3].key,
      label: candidateOptionFields[3].label,
      value: getDisplayText(item?.facility_name),
    },
    {
      key: candidateOptionFields[4].key,
      label: candidateOptionFields[4].label,
      value: getDisplayText(item?.part_name),
    },
    {
      key: candidateOptionFields[5].key,
      label: candidateOptionFields[5].label,
      value: getDisplayText(locationText),
    },
  ];
}

function buildCandidateOptionText(item, candidateOptionFields) {
  return buildCandidateOptionParts(item, candidateOptionFields)
    .map((part) => `${part.label} : ${part.value}`)
    .join(" ");
}

// HELPER: 선택된 이미지 타입에 맞는 보고서 이미지 소스를 우선순위대로 반환한다.
function getImageSource(images, detailedFile, imageType) {
  if (imageType === "ultrasonic") {
    return images?.image_visual ?? detailedFile?.image_visual ?? "";
  }

  if (imageType === "thermal") {
    return (
      images?.markup_image_triplespot ??
      images?.markup_image_thermal ??
      detailedFile?.markup_image_triplespot ??
      detailedFile?.markup_image_thermal ??
      ""
    );
  }

  return images?.image_uv ?? detailedFile?.image_uv ?? "";
}

// HELPER: 시계열 차트에 공통으로 쓰는 라벨/데이터셋 구조를 만든다.
function buildChartData(sortedFiles, labels) {
  return {
    labels: sortedFiles.map((item) =>
      getDisplayText(item?.shooting_date || item?.uploaded_at, item?.file_name),
    ),
    datasets: [
      {
        label: labels.maxTemperature,
        data: sortedFiles.map((item) => toNumeric(item?.thermal_max)),
        borderColor: "#d97706",
        backgroundColor: "#d97706",
        tension: 0.25,
        spanGaps: true,
      },
      {
        label: labels.averageUltrasonic,
        data: sortedFiles.map((item) => toNumeric(item?.avg_decibel)),
        borderColor: "#2563eb",
        backgroundColor: "#2563eb",
        tension: 0.25,
        spanGaps: true,
      },
    ],
  };
}

/*
 * TimeseriesReport 역할:
 * - 같은 설비의 시계열 데이터를 모아 추세 차트, KPI, 상세 비교 테이블을 구성한다.
 * - 사용자가 비교할 파일을 추가/제거하고 대표 이미지를 선택할 수 있게 관리한다.
 * - 선택된 파일들의 수치와 이미지를 바탕으로 초음파/온도 변화에 대한 요약 인사이트를 제공한다.
 *
 * 처리 흐름:
 * 1. 현재 파일을 기준으로 선택 상태, 후보 목록, 이미지 미리보기 상태를 초기화한다.
 * 2. 프로젝트 파일 목록과 선택된 파일의 상세 데이터/이미지를 비동기로 불러온다.
 * 3. 선택 결과를 바탕으로 차트 데이터, KPI, 요약 문장을 계산한다.
 * 4. 파일 추가/삭제, 대표 이미지 선택, 미리보기 같은 사용자 액션을 처리한다.
 * 5. 설정 바, 요약 영역, 차트, 상세 테이블, 이미지 미리보기를 순서대로 렌더링한다.
 */
export default function TimeseriesReport({
  file,
  sourceFile = file,
  projectId,
  userId,
  locale = "ko-KR",
  reportActionSlot = null,
  isReadOnly = false,
  handleChange = () => {},
  onSave = () => {},
  onCancel = () => {},
  isSaving = false,
  hasChanges = false,
}) {
  const { t, i18n } = useTranslation();
  const canRenderHwp = (i18n.resolvedLanguage ?? i18n.language ?? "")
    .toLowerCase()
    .startsWith("ko");
  const showAlert = useAppStore((s) => s.showAlert);
  const currentUploadId = String(sourceFile?.upload_id ?? "");
  const imageTypeOptions = [
    {
      value: "ultrasonic",
      label: t("imageFileIndex.timeseries.imageTypes.ultrasonic", {
        defaultValue: "초음파 이미지",
      }),
    },
    {
      value: "thermal",
      label: t("imageFileIndex.timeseries.imageTypes.thermal", {
        defaultValue: "열화상 이미지",
      }),
    },
    {
      value: "ultraviolet",
      label: t("imageFileIndex.timeseries.imageTypes.ultraviolet", {
        defaultValue: "자외선 이미지",
      }),
    },
  ];
  const candidateOptionFields = [
    {
      key: "uploaded_at",
      label: t("imageFileIndex.timeseries.candidateFields.uploadedAt", {
        defaultValue: "업로드일",
      }),
    },
    {
      key: "file_name",
      label: t("imageFileIndex.timeseries.candidateFields.fileName", {
        defaultValue: "파일명",
      }),
    },
    {
      key: "facility_type",
      label: t("imageFileIndex.timeseries.candidateFields.facilityType", {
        defaultValue: "설비 구분",
      }),
    },
    {
      key: "facility_name",
      label: t("imageFileIndex.timeseries.candidateFields.facilityName", {
        defaultValue: "설비명",
      }),
    },
    {
      key: "part_name",
      label: t("imageFileIndex.timeseries.candidateFields.partName", {
        defaultValue: "부품명",
      }),
    },
    {
      key: "location",
      label: t("imageFileIndex.timeseries.candidateFields.location", {
        defaultValue: "위치",
      }),
    },
  ];
  const [imageType, setImageType] = useState("ultrasonic");
  const selectedImageTypeLabel =
    imageTypeOptions.find((option) => option.value === imageType)?.label ||
    imageTypeOptions[0]?.label ||
    "-";
  const [candidateFiles, setCandidateFiles] = useState([]);
  const [selectedUploadIds, setSelectedUploadIds] = useState(
    currentUploadId ? [currentUploadId] : [],
  );
  const [representativeUploadIds, setRepresentativeUploadIds] = useState(
    currentUploadId ? [currentUploadId] : [],
  );
  const [selectedFiles, setSelectedFiles] = useState(
    sourceFile ? [sourceFile] : [],
  );
  const [fileImages, setFileImages] = useState({});
  const [pendingUploadIds, setPendingUploadIds] = useState([]);
  const [isCandidateLoading, setIsCandidateLoading] = useState(false);
  const [isSelectionLoading, setIsSelectionLoading] = useState(false);
  const [candidateError, setCandidateError] = useState("");
  const [selectionError, setSelectionError] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [isAddDropdownOpen, setIsAddDropdownOpen] = useState(false);
  const [temperatureChartMode, setTemperatureChartMode] =
    useState("thermalCenter");
  const addDropdownRef = useRef(null);
  const ultrasonicChartRef = useRef(null);
  const temperatureChartRef = useRef(null);
  const isEditMode = isReadOnly;
  const closeAddDropdown = () => {
    setIsAddDropdownOpen(false);
    setPendingUploadIds([]);
  };
  const handleToggleAddDropdown = () => {
    if (isAddDropdownOpen) {
      closeAddDropdown();
      return;
    }

    setIsAddDropdownOpen(true);
  };

  useEffect(() => {
    if (!isEditMode) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("reportEditModalOpen");

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("reportEditModalOpen");
    };
  }, [isEditMode, onCancel]);

  // STEP 1: 드롭다운 바깥 클릭 시 후보 선택 UI를 닫는다.
  useClickOutSide([addDropdownRef], () => {
    closeAddDropdown();
  });

  // STEP 1: 기준 파일이 바뀌면 선택 상태를 현재 파일 기준으로 다시 초기화한다.
  useEffect(() => {
    setSelectedUploadIds(currentUploadId ? [currentUploadId] : []);
    setRepresentativeUploadIds(currentUploadId ? [currentUploadId] : []);
    setPendingUploadIds([]);
  }, [currentUploadId]);

  // STEP 2: 같은 프로젝트 안에서 비교 가능한 후보 파일 목록을 불러온다.
  useEffect(() => {
    if (!projectId || !sourceFile?.upload_id) {
      setCandidateFiles(sourceFile ? [sourceFile] : []);
      setCandidateError("");
      setIsCandidateLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsCandidateLoading(true);
      setCandidateError("");

      const { success, files, message } = await getFileList(projectId, {
        sortKey: "shooting_date",
        sortDir: "asc",
        page: 1,
        limit: 500,
        excludeFileType: "video",
      });

      if (cancelled) return;

      if (!success) {
        setCandidateFiles(sourceFile ? [sourceFile] : []);
        setCandidateError(
          message ||
            t("imageFileIndex.timeseries.messages.projectFileListLoadFailed", {
              defaultValue: "프로젝트 파일 목록을 불러오지 못했습니다.",
            }),
        );
        setIsCandidateLoading(false);
        return;
      }

      const merged = [...files];
      if (!merged.some((item) => getUploadId(item) === currentUploadId)) {
        merged.push(sourceFile);
      }

      merged.sort(compareByShootingDate);
      setCandidateFiles(merged);
      setIsCandidateLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, sourceFile, currentUploadId, t]);

  // STEP 2: 현재 선택된 업로드 id 기준으로 상세 파일 데이터와 리포트 이미지를 함께 불러온다.
  useEffect(() => {
    if (!projectId || !userId || selectedUploadIds.length === 0) {
      setSelectedFiles(sourceFile ? [sourceFile] : []);
      setFileImages({});
      setSelectionError("");
      setIsSelectionLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsSelectionLoading(true);
      setSelectionError("");

      const results = await Promise.all(
        selectedUploadIds.map(async (uploadId) => {
          const isCurrentFile = String(uploadId) === currentUploadId;
          const fileResult = isCurrentFile
            ? { success: true, data: sourceFile }
            : await getFile(projectId, uploadId, userId, "", "", locale);
          const imageResult = await getImageBase64(uploadId, "image_report");

          return { uploadId, fileResult, imageResult };
        }),
      );

      if (cancelled) return;

      const nextFiles = [];
      const nextImages = {};
      let nextError = "";

      results.forEach(({ uploadId, fileResult, imageResult }) => {
        if (!fileResult?.success || !fileResult?.data) {
          nextError =
            nextError ||
            fileResult?.message ||
            t("imageFileIndex.timeseries.messages.selectedFileLoadFailed", {
              defaultValue: "선택한 파일 정보를 불러오지 못했습니다.",
            });
          return;
        }

        nextFiles.push(fileResult.data);
        nextImages[String(uploadId)] = imageResult?.success
          ? imageResult.data
          : {};
      });

      nextFiles.sort(compareByShootingDate);
      setSelectedFiles(nextFiles);
      setFileImages(nextImages);
      setSelectionError(nextError);
      setIsSelectionLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    userId,
    locale,
    sourceFile,
    currentUploadId,
    selectedUploadIds,
    t,
  ]);

  // STEP 2: 선택 해제된 파일은 대표 이미지 목록에서도 함께 정리한다.
  useEffect(() => {
    setRepresentativeUploadIds((prev) => {
      const selectedSet = new Set(selectedUploadIds.map(String));
      const next = prev.filter((uploadId) => selectedSet.has(String(uploadId)));
      return next.slice(0, 3);
    });
  }, [selectedUploadIds]);

  // STEP 2: 이미지 미리보기가 열려 있을 때 body scroll lock과 ESC 닫기를 관리한다.
  useEffect(() => {
    if (!previewImage) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPreviewImage(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewImage]);

  // STEP 3: 선택된 파일 집합으로 차트, KPI, 요약 문장에 필요한 파생 데이터를 계산한다.
  const selectedUploadIdSet = new Set(selectedUploadIds);
  const representativeUploadIdSet = new Set(
    representativeUploadIds.map(String),
  );
  const addableFiles = candidateFiles.filter(
    (item) => !selectedUploadIdSet.has(getUploadId(item)),
  );
  const selectedPendingCandidates = addableFiles.filter((item) =>
    pendingUploadIds.includes(getUploadId(item)),
  );
  const sortedSelectedFiles = [...selectedFiles].sort(compareByShootingDate);
  const periodStart = sortedSelectedFiles[0];
  const periodEnd = sortedSelectedFiles[sortedSelectedFiles.length - 1];
  const chartData = buildChartData(sortedSelectedFiles, {
    maxTemperature: t("imageFileIndex.timeseries.chart.labels.maxTemperature", {
      defaultValue: "최고 온도 (°C)",
    }),
    averageUltrasonic: t(
      "imageFileIndex.timeseries.chart.labels.averageUltrasonic",
      {
        defaultValue: "초음파 평균 (dB)",
      },
    ),
  });
  const ultrasonicChartData = {
    ...chartData,
    datasets: chartData.datasets[1] ? [chartData.datasets[1]] : [],
  };
  const temperatureCenterChartData = buildSingleMetricChartData(
    sortedSelectedFiles,
    {
      label: t("imageFileIndex.timeseries.chart.labels.temperature", {
        defaultValue: "온도 (°C)",
      }),
      getValue: (item) => toNumeric(item?.thermal_center),
      borderColor: "#d97706",
      backgroundColor: "#d97706",
    },
  );
  const threePhaseDiffChartData = buildSingleMetricChartData(
    sortedSelectedFiles,
    {
      label: t("imageFileIndex.timeseries.chart.labels.threePhaseDifference", {
        defaultValue: "3상 비교 온도차 (°C)",
      }),
      getValue: (item) => getThreePhaseDifferenceValue(item?.click_points),
      borderColor: "#0f766e",
      backgroundColor: "#0f766e",
      excludeMissing: true,
    },
  );
  const temperatureChartData =
    temperatureChartMode === "threePhaseDiff"
      ? threePhaseDiffChartData
      : temperatureCenterChartData;
  const chartOptions = buildChartOptions();
  const averageUltrasonic = averageNumeric(
    sortedSelectedFiles.map((item) => toNumeric(item?.avg_decibel)),
  );
  const averageTemperature = averageNumeric(
    sortedSelectedFiles.map((item) => toNumeric(item?.thermal_center)),
  );
  const threePhaseDiffValues = sortedSelectedFiles
    .map((item) => getThreePhaseDifferenceValue(item?.click_points))
    .filter((value) => value != null);
  const threePhaseCoveragePercent = sortedSelectedFiles.length
    ? Math.round(
        (threePhaseDiffValues.length / sortedSelectedFiles.length) * 100,
      )
    : 0;
  const threePhaseCoverageTone = getCoverageTone(threePhaseCoveragePercent);
  const averageThreePhaseDiffValue =
    threePhaseDiffValues.length > 0
      ? averageNumeric(threePhaseDiffValues)
      : "-";
  const averageThreePhaseDiffText =
    threePhaseDiffValues.length > 0
      ? `${averageThreePhaseDiffValue} °C`
      : t("imageFileIndex.timeseries.messages.notApplicable", {
          defaultValue: "해당 없음",
        });
  const statusDistribution = sortedSelectedFiles.reduce(
    (acc, item) => {
      const normalizedStatus = normalizeFusionStatusCategory(
        item?.fusion_result_code ?? item?.fusion_result,
      );

      if (normalizedStatus) {
        acc[normalizedStatus] += 1;
      }

      return acc;
    },
    { normal: 0, warning: 0, abnormal: 0 },
  );
  const statusDistributionTotal =
    statusDistribution.normal +
    statusDistribution.warning +
    statusDistribution.abnormal;
  const normalRatio =
    statusDistributionTotal > 0
      ? Math.round((statusDistribution.normal / statusDistributionTotal) * 100)
      : 0;
  const warningRatio =
    statusDistributionTotal > 0
      ? Math.round((statusDistribution.warning / statusDistributionTotal) * 100)
      : 0;
  const statusDistributionPercentages =
    statusDistributionTotal > 0
      ? {
          normal: normalRatio,
          warning: warningRatio,
          abnormal: Math.max(0, 100 - normalRatio - warningRatio),
        }
      : {
          normal: 0,
          warning: 0,
          abnormal: 0,
        };
  const ultrasonicOpinion = buildUltrasonicOpinion(sortedSelectedFiles, t);
  const temperatureOpinion = buildTemperatureOpinion(
    sortedSelectedFiles,
    averageThreePhaseDiffText ===
      t("imageFileIndex.timeseries.messages.notApplicable", {
        defaultValue: "해당 없음",
      })
      ? "-"
      : averageThreePhaseDiffText,
    t,
  );
  const generatedComprehensiveOpinion = buildTimeseriesComprehensiveOpinion({
    files: sortedSelectedFiles,
    startUploadId: currentUploadId,
    t,
  });
  const summaryOpinionText = generatedComprehensiveOpinion;
  const hasSummaryOpinion = Boolean(summaryOpinionText.trim());

  // STEP 3: 후보 목록이 바뀌면 추가 대기 중인 파일 선택도 유효한 항목만 남긴다.
  useEffect(() => {
    const addableUploadIdSet = new Set(
      addableFiles.map((item) => getUploadId(item)),
    );
    setPendingUploadIds((prev) => {
      const next = prev.filter((uploadId) =>
        addableUploadIdSet.has(String(uploadId)),
      );

      if (
        next.length === prev.length &&
        next.every((uploadId, index) => uploadId === prev[index])
      ) {
        return prev;
      }

      return next;
    });
  }, [addableFiles]);

  // STEP 4: 드롭다운에서 고른 후보 파일들을 현재 비교 대상에 추가한다.
  const handleAddFile = () => {
    if (pendingUploadIds.length === 0) return;

    setSelectedUploadIds((prev) => {
      const next = [...prev];
      pendingUploadIds.forEach((uploadId) => {
        if (!next.includes(uploadId)) {
          next.push(uploadId);
        }
      });
      return next;
    });
    setPendingUploadIds([]);
    setIsAddDropdownOpen(false);
  };

  // STEP 4: 추가 후보 목록에서 임시 선택 상태를 토글한다.
  const handlePendingCandidateToggle = (uploadId) => {
    setPendingUploadIds((prev) =>
      prev.includes(uploadId)
        ? prev.filter((item) => item !== uploadId)
        : [...prev, uploadId],
    );
  };

  // STEP 4: 현재 파일을 제외한 비교 대상을 제거한다.
  const handleRemoveFile = (uploadId) => {
    if (String(uploadId) === currentUploadId) return;

    setSelectedUploadIds((prev) =>
      prev.filter((item) => String(item) !== String(uploadId)),
    );
    setRepresentativeUploadIds((prev) =>
      prev.filter((item) => String(item) !== String(uploadId)),
    );
  };

  // STEP 4: 상세 테이블에서 대표 이미지로 노출할 파일을 최대 3개까지 관리한다.
  const handleRepresentativeChange = (uploadId, checked) => {
    const normalizedUploadId = String(uploadId);
    const maxRepresentativeMessage = t(
      "imageFileIndex.timeseries.messages.maxRepresentativeImages",
      {
        defaultValue:
          "보고서에 사용할 이미지는 최대 3개까지 선택할 수 있습니다.",
      },
    );

    setRepresentativeUploadIds((prev) => {
      const normalizedPrev = prev.map(String);

      if (checked) {
        if (
          normalizedPrev.includes(normalizedUploadId) ||
          normalizedPrev.length >= 3
        ) {
          if (
            !normalizedPrev.includes(normalizedUploadId) &&
            normalizedPrev.length >= 3
          ) {
            showAlert({
              text: maxRepresentativeMessage,
              success: false,
              autoClose: true,
            });
          }
          return normalizedPrev;
        }

        return [...normalizedPrev, normalizedUploadId];
      }

      return normalizedPrev.filter((item) => item !== normalizedUploadId);
    });
  };

  // STEP 5: 설정 바, 요약 정보, 차트, 상세 비교표, 이미지 미리보기를 렌더링한다.
  return (
    <div className="equipmentTimeseriesReport reportShell">
      <div className="reportPanelActionBar">
        <div className="reportPanelDownloadActions">
          <TimeseriesExcelDownloadBtn
            file={file}
            selectedFiles={sortedSelectedFiles}
            selectedImageTypeLabel={selectedImageTypeLabel}
            fileImages={fileImages}
            imageType={imageType}
            representativeUploadIdSet={representativeUploadIdSet}
            ultrasonicChartRef={ultrasonicChartRef}
            temperatureChartRef={temperatureChartRef}
            temperatureChartMode={temperatureChartMode}
            averageUltrasonic={averageUltrasonic}
            averageTemperature={averageTemperature}
            averageThreePhaseDiffValue={averageThreePhaseDiffValue}
            comprehensiveOpinion={summaryOpinionText}
            ultrasonicOpinion={ultrasonicOpinion}
            temperatureOpinion={temperatureOpinion}
          />
          {canRenderHwp ? (
            <TimeseriesHmlDownloadBtn
              file={file}
              selectedFiles={sortedSelectedFiles}
              selectedImageTypeLabel={selectedImageTypeLabel}
              imageType={imageType}
              representativeUploadIdSet={representativeUploadIdSet}
              ultrasonicChartRef={ultrasonicChartRef}
              temperatureChartRef={temperatureChartRef}
              averageUltrasonic={averageUltrasonic}
              averageTemperature={averageTemperature}
              averageThreePhaseDiffValue={averageThreePhaseDiffValue}
              comprehensiveOpinion={summaryOpinionText}
            />
          ) : null}
        </div>
        {reportActionSlot ? (
          <div className="reportPanelEditActions">{reportActionSlot}</div>
        ) : null}
      </div>

      <TimeseriesConfigBar
        imageType={imageType}
        onImageTypeChange={(event) => setImageType(event.target.value)}
        imageTypeOptions={imageTypeOptions}
        candidateOptionFields={candidateOptionFields}
        addDropdownRef={addDropdownRef}
        isAddDropdownOpen={isAddDropdownOpen}
        onToggleAddDropdown={handleToggleAddDropdown}
        isCandidateLoading={isCandidateLoading}
        addableFiles={addableFiles}
        selectedPendingCandidates={selectedPendingCandidates}
        buildCandidateOptionText={(item) =>
          buildCandidateOptionText(item, candidateOptionFields)
        }
        buildCandidateOptionParts={(item) =>
          buildCandidateOptionParts(item, candidateOptionFields)
        }
        getUploadId={getUploadId}
        pendingUploadIds={pendingUploadIds}
        onPendingCandidateToggle={handlePendingCandidateToggle}
        onAddFile={handleAddFile}
        selectedFiles={sortedSelectedFiles}
        currentUploadId={currentUploadId}
        onRemoveFile={handleRemoveFile}
        getDisplayText={getDisplayText}
      />

      <TimeseriesAnalyticsSection
        file={file}
        periodStart={periodStart}
        periodEnd={periodEnd}
        getDisplayText={getDisplayText}
        hasSummaryOpinion={hasSummaryOpinion}
        comprehensiveOpinion={summaryOpinionText}
        ultrasonicOpinion={ultrasonicOpinion}
        temperatureOpinion={temperatureOpinion}
        sortedSelectedFiles={sortedSelectedFiles}
        averageUltrasonic={averageUltrasonic}
        averageTemperature={averageTemperature}
        averageThreePhaseDiffText={averageThreePhaseDiffText}
        threePhaseCoverageTone={threePhaseCoverageTone}
        threePhaseCoveragePercent={threePhaseCoveragePercent}
        threePhaseDiffValues={threePhaseDiffValues}
        statusDistribution={statusDistribution}
        statusDistributionTotal={statusDistributionTotal}
        statusDistributionPercentages={statusDistributionPercentages}
        chartOptions={chartOptions}
        ultrasonicChartData={ultrasonicChartData}
        temperatureChartData={temperatureChartData}
        temperatureChartMode={temperatureChartMode}
        onTemperatureChartModeChange={setTemperatureChartMode}
        threePhaseDiffChartData={threePhaseDiffChartData}
        ultrasonicChartRef={ultrasonicChartRef}
        temperatureChartRef={temperatureChartRef}
      />

      <TimeseriesDataTableGroup
        sortedSelectedFiles={sortedSelectedFiles}
        fileImages={fileImages}
        imageType={imageType}
        getImageSource={getImageSource}
        representativeUploadIdSet={representativeUploadIdSet}
        representativeCount={representativeUploadIds.length}
        onRepresentativeChange={handleRepresentativeChange}
        onRemoveFile={handleRemoveFile}
        currentUploadId={currentUploadId}
        onPreviewImage={setPreviewImage}
        getDisplayText={getDisplayText}
        formatMetric={formatMetric}
        formatThreePhaseDifference={(clickPoints) =>
          formatThreePhaseDifference(clickPoints, {
            emptyText: t("imageFileIndex.timeseries.messages.notApplicable", {
              defaultValue: "해당 없음",
            }),
          })
        }
      />

      {candidateError ? (
        <p className="equipmentTimeseriesReport errorText">{candidateError}</p>
      ) : null}
      {selectionError ? (
        <p className="equipmentTimeseriesReport errorText">{selectionError}</p>
      ) : null}
      {isSelectionLoading ? (
        <p className="equipmentTimeseriesReport loadingText">
          {t("imageFileIndex.timeseries.messages.loadingSelectionInfo", {
            defaultValue: "선택한 파일 정보를 불러오는 중입니다.",
          })}
        </p>
      ) : null}

      <TimeseriesPreviewModal
        previewImage={previewImage}
        onClose={() => setPreviewImage(null)}
      />

      {isEditMode ? (
        <TimeseriesEditModal
          file={file}
          sourceInspectionResult={sourceFile?.inspection_result ?? ""}
          generatedOpinion={summaryOpinionText}
          handleChange={handleChange}
          onCancel={onCancel}
          onSave={onSave}
          isSaving={isSaving}
          hasChanges={hasChanges}
          t={t}
        />
      ) : null}
    </div>
  );
}
